import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Fade, Lines, Reveal } from "@/components/motion";
import { BookingButton, WhatsAppAssist, WhatsAppButton } from "@/components/CTA";

const BENEFITS = [
  ["Percorso più coerente", "Alimentazione e allenamento seguono la stessa logica professionale."],
  ["Obiettivi allineati", "Piano nutrizionale e programma motorio lavorano nella stessa direzione."],
  ["Meno contraddizioni", "Nessun conflitto tra ciò che mangi e come ti alleni."],
  ["Organizzazione più semplice", "Un unico riferimento per due aree del tuo percorso."],
  ["Monitoraggio integrato", "Progressi valutati insieme, con aggiustamenti coordinati nel tempo."],
  ["Più praticità", "Meno professionisti da coordinare, più chiarezza nella vita di tutti i giorni."],
];

export default function Integrati() {
  useSEO(SEO.integrati);
  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-20 sm:pb-28">
        <div className="grid items-start gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
          <div>
            <Fade>
              <p className="eyebrow text-leaf-deep">Percorso integrato · Bologna e Casalecchio di Reno</p>
            </Fade>
            <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <Lines
                delay={0.2}
                lines={["Nutrizione e allenamento", <em key="e" className="text-leaf-deep">integrati</em>]}
              />
            </h1>
            <Fade delay={0.6}>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/65">
                Quando alimentazione e allenamento sono coordinati nella stessa logica
                professionale, il percorso diventa più coerente, più pratico e più semplice da
                seguire.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/60">
                Nutrizione e allenamento restano due servizi indipendenti: puoi sceglierne anche
                uno solo. Il percorso integrato è un'opportunità in più per chi desidera un lavoro
                coordinato — mai un obbligo.
              </p>
            </Fade>
          </div>
          <Fade delay={0.4} className="relative">
            <img
              src={SITE.images.integrated}
              alt="Nutrizione e allenamento insieme: alimentazione equilibrata, attività fisica e il brand Nutrimove Lab"
              className="w-full [filter:drop-shadow(0_28px_36px_rgba(16,22,20,0.14))]"
              width={1100}
              height={1375}
              fetchPriority="high"
              data-testid="integrated-visual"
            />
          </Fade>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="I vantaggi del percorso integrato">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <p className="eyebrow text-wave-deep">Perché integrare</p>
            <h2 className="mt-6 max-w-2xl font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Due aree, coordinate intorno a te
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="border-t-2 border-leaf/60 pt-5">
                  <p className="font-display italic text-sm text-leaf-deep">0{i + 1}</p>
                  <h3 className="mt-2 text-sm font-bold uppercase tracking-[0.16em]">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10" aria-label="Libertà di scelta">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className="font-display text-2xl sm:text-3xl italic leading-snug tracking-tight text-ink/85 text-balance">
              “Un solo professionista, un'unica visione: nutrizione e movimento che lavorano
              insieme.”
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="Inizia il percorso integrato">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 py-20 sm:py-28 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Vuoi un percorso unico e coordinato?
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">
              Il primo passo è una prima visita integrata: raccoglieremo le informazioni utili,
              definiremo gli obiettivi e imposteremo un percorso personalizzato di nutrizione e
              allenamento.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <BookingButton label="Prenota una visita" testId="integrated-booking-cta" />
              <WhatsAppButton testId="integrated-whatsapp-cta" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <WhatsAppAssist testId="integrated-whatsapp-assist" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
